import "@mui/x-data-grid/themeAugmentation";
import { createTheme, PaletteOptions } from "@mui/material/styles";
import { alpha } from "@mui/material";

// Light Palette
export const lightPalette: PaletteOptions = {
	mode: "light",
	text: {
		primary: "#666666",
		secondary: "#B3B3B3",
		disabled: "#cccccc",
	},
	primary: {
		main: "#2357BE",
		light: "#019BD9",
	},
	secondary: {
		main: "#585B6D",
	},
	success: {
		main: "#009245",
	},
	warning: {
		main: "#FFB507",
	},
	error: {
		main: "#FF0000",
	},
	action: {
		disabled: "#ffffff",
	},
};

// Dark Palette
export const darkPalette: PaletteOptions = {
	mode: "dark",
	background: {
		default: "#1E1E1E",
	},
	text: {
		primary: "#B3B3B3",
		secondary: "#b0b0b0",
		disabled: "#5D5D5D",
	},
	primary: {
		main: "#2357BE",
		light: "#019BD9",
	},
	secondary: {
		main: "#585B6D",
	},
	success: {
		main: "#009245",
	},
	warning: {
		main: "#FFB507",
	},
	error: {
		main: "#FF0000",
	},
	action: {
		disabled: "#333333",
	},
};
const SANS_SERIF = "sans-serif";
const SYSTEM_UI = "system-ui";
const MONTSERRAT = "Montserrat";
const PT_SANS_PRO_NARROW = "PT Sans Pro Narrow";
const MONTSERRAT_SANS_SERIF = `${MONTSERRAT}, ${SANS_SERIF}`;
const PT_SANS_PRO_NARROW_SANS_SERIF = `${PT_SANS_PRO_NARROW}, ${SANS_SERIF}`;

export const createAppTheme = (palette: PaletteOptions) =>
	createTheme({
		palette,
		typography: {
			fontFamily: [MONTSERRAT, SYSTEM_UI, SANS_SERIF].join(","),

			h1: { fontSize: "1.25rem", lineHeight: "normal", fontWeight: 500 }, //20px
			h2: { fontSize: "1.5rem", lineHeight: "normal", fontWeight: 500 }, //24px
			h3: { fontSize: "1.125rem", lineHeight: "normal", fontWeight: 500 }, //18px
			appBody1: {
				fontSize: "0.75rem", //12px
				lineHeight: "normal",
				fontWeight: 500,
				fontFamily: MONTSERRAT_SANS_SERIF,
			},
			appBody2: {
				fontSize: "0.75rem", //12px
				lineHeight: "normal",
				fontWeight: 400,
				fontFamily: PT_SANS_PRO_NARROW_SANS_SERIF,
			},
			appLabel: {
				fontSize: "0.90625rem", //14.5px
				lineHeight: "normal",
				fontWeight: 400,
				fontFamily: PT_SANS_PRO_NARROW_SANS_SERIF,
			},
			appInfoText: {
				fontSize: "0.875rem", //14px
				lineHeight: "normal",
				fontWeight: 400,
				fontFamily: PT_SANS_PRO_NARROW_SANS_SERIF,
			},
			caption: { fontSize: "0.625rem", lineHeight: "normal", fontWeight: 400 },
		},

		components: {
			MuiButton: {
				defaultProps: {
					disableElevation: true,
				},
				styleOverrides: {
					root: () => ({
						borderRadius: 6,
						textTransform: "none",
						fontFamily: PT_SANS_PRO_NARROW_SANS_SERIF,
						fontSize: "0.875rem",
						boxShadow: "none",
						height: 30,
					}),

					contained: ({ theme, ownerState }) => {
						const colorKey = ownerState.color ?? "primary";
						const paletteEntry = (theme.palette as any)[colorKey] ?? theme.palette.primary;
						const main = paletteEntry.main ?? theme.palette.primary.main;

						return {
							"&:hover": {
								backgroundColor: main,
								boxShadow: `0 0 10px 2px ${alpha(main, 0.5)}`,
							},
							"&:hover.Mui-disabled": {
								// cursor: "not-allowed",
							},
							"&:active": {
								boxShadow: `0 0 8px 1px ${alpha(main, 0.65)}`,
							},
							"&.Mui-disabled": {
								color: theme.palette.action.disabled,
								backgroundColor: theme.palette.action.disabledBackground,
								boxShadow: "none",
								// pointerEvents: "auto",
							},
						};
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					columnHeaderTitle: {
						fontFamily: MONTSERRAT_SANS_SERIF,
						fontWeight: 500,
						fontSize: "0.75rem",
						lineHeight: "normal",
						color: "var(--text-color)",
						whiteSpace: "normal",
						textOverflow: "clip",
					},
					cell: {
						fontFamily: PT_SANS_PRO_NARROW_SANS_SERIF,
						fontWeight: 400,
						fontSize: "0.75rem",
						lineHeight: "normal",
						color: "var(--text-color)",
						alignContent: "center",
					},
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: ({ theme }) => ({
						...theme.typography.h2,
					}),
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: ({ theme }) => {
						const color = theme.palette.text.primary;

						return {
							color,
							"&.Mui-disabled": {
								cursor: "not-allowed",
								color: theme.palette.text.disabled,
							},
						};
					},
				},
			},
			MuiFormControlLabel: {
				styleOverrides: {
					label: ({ theme }) => ({
						...(theme.typography as any).appLabel,

						"&.Mui-disabled": {
							color: theme.palette.text.disabled,
						},
					}),
				},
			},
		},
	});
